import { Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface VoiceFilterBarProps {
  filterOptions: {
    accents: string[];
    genders: string[];
    ages: string[];
    languages: string[];
    categories: string[];
  };
  selectedCategory?: string;
  selectedAccent: string;
  selectedLanguage?: string;
  selectedGender: string;
  selectedAge: string;
  onCategoryChange?: (category: string) => void;
  onAccentChange: (accent: string) => void;
  onLanguageChange?: (language: string) => void;
  onGenderChange: (gender: string) => void;
  onAgeChange: (age: string) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
  onFiltersChange?: (filters: {
    category: string;
    accent: string;
    language: string;
    gender: string;
    age: string;
  }) => void;
}

export const VoiceFilterBar = ({
  filterOptions,
  selectedCategory = "all",
  selectedAccent,
  selectedLanguage = "all",
  selectedGender,
  selectedAge,
  onCategoryChange,
  onAccentChange,
  onLanguageChange,
  onGenderChange,
  onAgeChange,
  onClearFilters,
  hasActiveFilters,
  onFiltersChange
}: VoiceFilterBarProps) => {
  const handleCategoryChange = (category: string) => {
    onCategoryChange?.(category);
    if (onFiltersChange) {
      onFiltersChange({
        category,
        accent: selectedAccent,
        language: selectedLanguage,
        gender: selectedGender,
        age: selectedAge
      });
    }
  };

  const handleAccentChange = (accent: string) => {
    onAccentChange(accent);
    if (onFiltersChange) {
      onFiltersChange({
        category: selectedCategory,
        accent,
        language: selectedLanguage,
        gender: selectedGender,
        age: selectedAge
      });
    }
  };

  const handleLanguageChange = (language: string) => {
    onLanguageChange?.(language);
    if (onFiltersChange) {
      onFiltersChange({
        category: selectedCategory,
        accent: selectedAccent,
        language,
        gender: selectedGender,
        age: selectedAge
      });
    }
  };

  const handleGenderChange = (gender: string) => {
    onGenderChange(gender);
    if (onFiltersChange) {
      onFiltersChange({
        category: selectedCategory,
        accent: selectedAccent,
        language: selectedLanguage,
        gender,
        age: selectedAge
      });
    }
  };

  const handleAgeChange = (age: string) => {
    onAgeChange(age);
    if (onFiltersChange) {
      onFiltersChange({
        category: selectedCategory,
        accent: selectedAccent,
        language: selectedLanguage,
        gender: selectedGender,
        age
      });
    }
  };

  return (
    <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200 flex-shrink-0">
      <div className="flex items-center gap-2 mb-3">
        <Filter className="h-4 w-4 text-gray-600" />
        <span className="text-sm font-medium text-gray-700">Filter Voices</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <div>
          <label className="text-xs text-gray-600 mb-1 block">Category</label>
          <Select value={selectedCategory} onValueChange={handleCategoryChange}>
            <SelectTrigger className="h-9 text-sm">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {filterOptions.categories.map(category => (
                <SelectItem key={category} value={category}>{category}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-gray-600 mb-1 block">Accent</label>
          <Select value={selectedAccent} onValueChange={handleAccentChange}>
            <SelectTrigger className="h-9 text-sm">
              <SelectValue placeholder="All Accents" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Accents</SelectItem>
              {filterOptions.accents.map(accent => (
                <SelectItem key={accent} value={accent}>{accent}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-gray-600 mb-1 block">Language</label>
          <Select value={selectedLanguage} onValueChange={handleLanguageChange}>
            <SelectTrigger className="h-9 text-sm">
              <SelectValue placeholder="All Languages" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Languages</SelectItem>
              {filterOptions.languages.map(language => (
                <SelectItem key={language} value={language}>{language}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-gray-600 mb-1 block">Gender</label>
          <Select value={selectedGender} onValueChange={handleGenderChange}>
            <SelectTrigger className="h-9 text-sm">
              <SelectValue placeholder="All Genders" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Genders</SelectItem>
              {filterOptions.genders.map(gender => (
                <SelectItem key={gender} value={gender}>{gender}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-gray-600 mb-1 block">Age</label>
          <Select value={selectedAge} onValueChange={handleAgeChange}>
            <SelectTrigger className="h-9 text-sm">
              <SelectValue placeholder="All Ages" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Ages</SelectItem>
              {filterOptions.ages.map(age => (
                <SelectItem key={age} value={age}>{age}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearFilters}
          className="mt-3 text-xs h-7"
        >
          Clear All Filters
        </Button>
      )}
    </div>
  );
};
